/**
 * Las zonas horarias que se pueden elegir para una autoridad (HU-23-C4, RN-20) y cómo
 * se describen para que quien no sepa de nombres IANA elija la correcta.
 *
 * LA LISTA SALE DEL NAVEGADOR, NO DE UN ARCHIVO. `Intl.supportedValuesOf('timeZone')`
 * devuelve la base de datos IANA que trae el propio motor —la misma familia de datos
 * con la que el backend valida (`ZoneInfo`)—, así que no hay una segunda lista escrita
 * a mano que se pueda desincronizar: el defecto que este proyecto ya pagó con los 14
 * países duplicados en `paises.js`. Se acota a `America/` porque los 14 mercados del
 * alcance están todos ahí; ofrecer `Asia/Tokyo` solo abre la puerta a un error.
 *
 * LO QUE AYUDA A ELEGIR ES LA HORA, NO EL NOMBRE. «America/Bogota» no le dice nada a
 * quien configura la autoridad de Colombia; «ahora 11:32 · UTC-05:00» sí: se compara
 * con el reloj del país y se sabe si es la zona correcta. El desfase se calcula de
 * verdad para el instante dado —cambia con el horario de verano de Chile o Paraguay—,
 * no se escribe en una tabla.
 *
 * Todo es puro: `ahora` entra por parámetro para poder probarlo con una fecha fija.
 */

import type { OpcionDeSelect } from '../components/ui';

const PREFIJO_DEL_ALCANCE = 'America/';

/**
 * Las zonas IANA de América que conoce el navegador, ordenadas como él las entrega
 * (alfabéticamente). Vacío si el motor no expone la lista (`Intl.supportedValuesOf`
 * llegó a Chrome/Edge 99, Firefox 93 y Safari 15.4): la pantalla cae entonces al campo
 * de texto, que el dominio sigue validando.
 */
export function zonasHorariasDeAmerica(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone').filter((zona) => zona.startsWith(PREFIJO_DEL_ALCANCE));
  } catch {
    return [];
  }
}

/** Las partes de la fecha civil en una zona, para un instante dado. */
const partesEnZona = (zona: string, ahora: Date): Record<string, number> => {
  const partes = new Intl.DateTimeFormat('en-US', {
    timeZone: zona,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(ahora);
  const valores: Record<string, number> = {};
  for (const parte of partes) {
    if (parte.type !== 'literal') valores[parte.type] = Number(parte.value);
  }
  return valores;
};

/**
 * El desfase de la zona respecto de UTC en minutos, para ese instante. Se deriva de la
 * fecha civil que el motor calcula para la zona —no de una tabla de desfases—, así que
 * respeta el horario de verano vigente en `ahora`.
 */
export function desfaseEnMinutos(zona: string, ahora: Date): number {
  const p = partesEnZona(zona, ahora);
  const enZona = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  // Los milisegundos de `ahora` no existen en la fecha civil: se descartan antes de
  // restar, o el redondeo caería del lado equivocado en el límite del minuto.
  const enUtc = Math.floor(ahora.getTime() / 1000) * 1000;
  return Math.round((enZona - enUtc) / 60_000);
}

/** «UTC-05:00», «UTC+00:00», «UTC-03:30». */
export function etiquetaDeDesfase(minutos: number): string {
  const signo = minutos < 0 ? '-' : '+';
  const absoluto = Math.abs(minutos);
  const horas = String(Math.floor(absoluto / 60)).padStart(2, '0');
  const resto = String(absoluto % 60).padStart(2, '0');
  return `UTC${signo}${horas}:${resto}`;
}

/**
 * «ahora 11:32 · UTC-05:00»: lo que se muestra bajo cada zona en el desplegable. Una
 * zona que el motor no reconoce —solo puede pasar con el campo de texto de respaldo—
 * se describe como tal en vez de reventar la lista entera.
 */
export function describirZona(zona: string, ahora: Date = new Date()): string {
  try {
    const p = partesEnZona(zona, ahora);
    const hora = `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;
    return `ahora ${hora} · ${etiquetaDeDesfase(desfaseEnMinutos(zona, ahora))}`;
  } catch {
    return 'zona no reconocida';
  }
}

/**
 * Las opciones del desplegable. La zona ACTUAL de la autoridad —la que sembró el script
 * 021 o la que alguien tecleó antes de que existiera la lista— se conserva como primera
 * opción aunque no esté entre las de América: cambiar el control no puede hacer
 * desaparecer un valor guardado.
 */
export function opcionesDeZonaHoraria(
  zonas: readonly string[],
  actual: string | null | undefined,
  ahora: Date = new Date(),
): OpcionDeSelect[] {
  const vigente = (actual ?? '').trim();
  const lista = vigente && !zonas.includes(vigente) ? [vigente, ...zonas] : [...zonas];
  return lista.map((zona) => ({ value: zona, label: zona, hint: describirZona(zona, ahora) }));
}
